import { useMemo, useState } from 'react'
import { formatCurrency, booleanStringIsTruthy } from 'lib/helpers'
import { useProduct, useCart, flattenConnection } from '@shopify/hydrogen-react'
import { useAppStore } from 'lib/hooks/useAppStore'
import { ResponsiveImage } from 'components/Global/ResponsiveImage'
import { AnimatePresence, motion } from 'motion/react'
import Link from 'next/link'
import { Button } from 'components/Global/Button'
import { urlForImage } from 'lib/sanity.image'
import { ProductCardTags } from './ProductCardTags'

export const ProductCard = ({ product }) => {
  const {
    selectedVariant,
    setSelectedVariant,
    options,
    selectedOptions,
    isOptionInStock,
    setSelectedOption,
  } = useProduct()
  const { linesAdd, status } = useCart()
  const { cartState, setCartState } = useAppStore()
  const [hovered, setHovered] = useState(false)

  const addToCart = (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (selectedVariant?.id) {
      if (cartState === 'closed') {
        setCartState('expanded')
      }
      linesAdd([{ merchandiseId: selectedVariant.id, quantity: 1 }])
    }
  }

  const images = useMemo(() => {
    if (product?.photos) {
      const colorOption = selectedOptions?.Color
        ? product.photos.find(
            (photo) => photo.forOption === selectedOptions.Color
          )
        : product.photos[0]
      return colorOption?.photos ?? []
    }
    return product.images ? flattenConnection(product.images) : []
  }, [product.images, product.photos, selectedOptions])

  const hasMultipleOptions = useMemo(
    () =>
      options?.some((option) => option?.values && option?.values?.length > 1),
    [options]
  )

  const mainImage = useMemo(() => {
    if (!images.length) return ''
    return (
      images[0]?.src ||
      urlForImage(images[0]?.asset).auto('format').width(600).url()
    )
  }, [images])

  const secondaryImage = useMemo(() => {
    if (images.length < 2) return ''
    return (
      images[1]?.src ||
      urlForImage(images[1]?.asset).auto('format').width(600).url()
    )
  }, [images])

  const sizeOptions = useMemo(
    () => options?.find((option) => option?.name === 'Size')?.values ?? [],
    [options]
  )
  const colorOptions = useMemo(
    () => options?.find((option) => option?.name === 'Color')?.values ?? [],
    [options]
  )
  const isPreorder = useMemo(
    () => booleanStringIsTruthy(product.preorder?.value),
    [product]
  )
  const isBackorder = useMemo(
    () => booleanStringIsTruthy(product.backorder?.value),
    [product]
  )

  return (
    <Link
      scroll={false}
      className={`${
        product.availableForSale ? 'group' : 'opacity-60'
      } relative flex aspect-[2/3] items-end justify-center overflow-hidden`}
      href={`/products/${product.handle}`}
      onMouseEnter={() => {
        setHovered(true)
        if (!hasMultipleOptions) {
          if (options && options[0]) {
            const option = options[0] as any
            setSelectedOption(option.name, option.values[0])
          }
        }
      }}
      onMouseLeave={() => {
        setHovered(false)
        setSelectedVariant(null)
        options?.forEach((option: any) => {
          setSelectedOption(option.name, '')
        })
      }}
    >
      <ProductCardTags
        isPreorder={isPreorder}
        isBackorder={isBackorder}
        discount={product.discountAmount?.value}
      />
      <div className="absolute inset-0 z-[0] h-full w-full transition-opacity delay-[400ms] duration-300 md:group-hover:opacity-0 md:group-hover:delay-0">
        <ResponsiveImage
          src={mainImage}
          alt=""
          fill
          sizes="(min-width: 768px) 25vw, 50vw"
        />
      </div>
      {secondaryImage ? (
        <div className="ease absolute inset-0 z-[0] h-full w-full opacity-0 transition-opacity delay-0 duration-300 md:group-hover:opacity-100 md:group-hover:delay-[400ms]">
          <ResponsiveImage
            src={secondaryImage}
            alt=""
            fill
            sizes="(min-width: 768px) 25vw, 50vw"
          />
        </div>
      ) : null}
      {product.availableForSale ? (
        <div className="z-1 relative w-full px-14 pb-0 md:px-[28px] md:pb-40">
          <div className="relative mb-8 w-full rounded-8 bg-whiteTransparent text-11 leading-100 text-midGray md:text-13">
            {hasMultipleOptions ? (
              <div className="ease absolute inset-0 z-[5] hidden h-full w-full opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100">
                <div className="flex h-full w-full gap-[60px] rounded-8 bg-white px-[18px] py-4 text-11 leading-100">
                  {/* Color Selector */}
                  {colorOptions && colorOptions.length > 1 ? (
                    <div className="flex items-center gap-[5px]">
                      {colorOptions?.map((value, i) => {
                        if (!value) return null
                        const color = product.colors?.find(
                          (color) => color?.label === value
                        )
                        const available = isOptionInStock('Color', value)
                        const isActive =
                          selectedOptions && selectedOptions['Color'] === value

                        return (
                          <button
                            disabled={!available}
                            tabIndex={available ? 0 : -1}
                            className={`ease relative h-[18px] w-[18px] rounded-5 border-1 border-solid transition-colors duration-200 ${
                              isActive
                                ? 'border-[#aaa]'
                                : 'border-[transparent]'
                            } ${
                              available ? '' : 'cursor-not-allowed opacity-50'
                            }`}
                            key={`${value}-${i}-card`}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              available && setSelectedOption('Color', value)
                            }}
                            aria-label={`Select ${value}`}
                          >
                            <div
                              className="absolute left-1/2 top-1/2 h-[12px] w-[12px] -translate-x-1/2 -translate-y-1/2 rounded-3"
                              style={{
                                backgroundColor: color?.value ?? 'black',
                                border: '.5px solid black',
                              }}
                            ></div>
                          </button>
                        )
                      })}
                    </div>
                  ) : null}
                  {/* Size Selector */}
                  <div className="flex h-full flex-1 gap-4">
                    {sizeOptions?.map((value, i) => {
                      if (!value) return null
                      const available = isOptionInStock('Size', value)
                      const isActive =
                        selectedOptions && selectedOptions['Size'] === value

                      return (
                        <button
                          disabled={!available}
                          tabIndex={available ? 0 : -1}
                          className={`ease flex-1 rounded-6 transition-colors duration-200 ${
                            isActive && available ? 'bg-[#DEDEDE]' : 'bg-white'
                          } ${
                            available
                              ? 'hover:bg-[#DEDEDE]'
                              : 'cursor-not-allowed opacity-50'
                          }`}
                          key={`${value}-${i}-card`}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            available && setSelectedOption('Size', value)
                          }}
                        >
                          {value}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            ) : null}
            <div className="flex w-full justify-between px-10 py-8 md:px-16 md:py-12">
              <div className="pr-12">{product.title}</div>
              {product.availableForSale ? (
                <div className="flex gap-4">
                  {product.compareAtPriceRange?.minVariantPrice?.amount > 0 ? (
                    <div className="line-through opacity-50">
                      {formatCurrency(
                        product.compareAtPriceRange.minVariantPrice.amount
                      )}
                    </div>
                  ) : null}
                  <div>
                    {formatCurrency(product.priceRange.minVariantPrice.amount)}
                  </div>
                </div>
              ) : (
                <div className="text-12">Sold Out</div>
              )}
            </div>
          </div>
          <AnimatePresence mode="wait">
            {selectedVariant && hovered && selectedVariant.availableForSale ? (
              <motion.div
                className="relative hidden w-full md:block"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  duration: 0.35,
                  ease: [0.7, 0, 0.1, 0.8],
                }}
              >
                <Button
                  as="button"
                  onClick={addToCart}
                  className={`w-full bg-yellow p-12 text-center text-11 uppercase text-darkGray md:text-11 ${
                    status === 'updating' ? 'cursor-wait' : 'cursor-pointer'
                  }`}
                >
                  Add to cart
                </Button>
              </motion.div>
            ) : null}
            {selectedVariant && hovered && !selectedVariant.availableForSale ? (
              <motion.div
                className="relative hidden w-full md:block"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  duration: 0.35,
                  ease: [0.7, 0, 0.1, 0.8],
                }}
              >
                <Button
                  as="button"
                  disabled
                  className={`w-full cursor-not-allowed bg-yellow p-12 text-center text-11 uppercase text-darkGray hover:!bg-yellow md:text-11`}
                >
                  Sold Out
                </Button>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      ) : (
        <div className="z-1 relative w-full px-14 pb-0 md:px-20 md:px-[28px] md:pb-40">
          <div className="mb-8 w-full rounded-8 bg-borderGray p-10 text-center text-11 text-black">
            Sold Out
          </div>
        </div>
      )}
    </Link>
  )
}
